import { Box, Button, GlobalStyles, Paper, Stack, Typography } from '@mui/material'
import { QRCodeSVG } from 'qrcode.react'
import type { ReactElement } from 'react'
import { useCallback } from 'react'
import { Helmet } from 'react-helmet'
import { IoBatteryFullOutline, IoCashOutline, IoNotificationsOutline, IoPeopleOutline, IoPrintOutline, IoWifiOutline } from 'react-icons/io5'
import { baseURL, deviceCampaignLabel, deviceCampaignPriceNumber, devicePriceNumber, serviceName, supportEmail } from 'src/config'

const TINT = '#FFF6E5'

// 展示会のブース背面に貼るポスター用。A2縦1枚で完結 (noindex)。Flyer(ペライチ)を参考に大判化。
export const Poster = () => {
  const handlePrint = useCallback(() => {
    window.print()
  }, [])

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="robots" content="noindex, nofollow" />
        <title>{serviceName} | 展示会ポスター (A2)</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      {/* 印刷 / PDF出力時のスタイル。A2縦ちょうど1枚のシートにする */}
      <GlobalStyles
        styles={{
          '@media print': {
            '*': { WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' },
            'header, footer, .no-print': { display: 'none !important' },
            // アプリのレイアウトラッパー(main配下)の余白・幅制限・flexを打ち消す。
            // 特に content Box の maxWidth:980 が残るとA2(≈1587px)では箱が左寄せになりポスターが左に寄る
            'main, main > div, main > div > div': {
              margin: '0 !important',
              padding: '0 !important',
              maxWidth: 'none !important',
              width: 'auto !important',
              display: 'block !important',
            },
            body: { backgroundColor: '#fff' },
            img: { maxWidth: '100% !important' },
            '.poster-root': { maxWidth: 'none !important', margin: '0 !important', padding: '0 !important' },
            // A2縦ちょうど1枚 (@page margin:0 のため余白はpaddingで作る)。overflow:hiddenで2ページ目を防ぐ
            '.poster-page': {
              boxSizing: 'border-box',
              width: '420mm',
              height: '594mm',
              margin: '0 auto !important',
              padding: '26mm 28mm 24mm',
              overflow: 'hidden',
              boxShadow: 'none !important',
              border: 'none !important',
              justifyContent: 'space-between',
            },
            '.poster-page > * + *': { marginTop: '0 !important' },
            '.poster-qr svg': { width: '190px !important', height: '190px !important', display: 'block' },
          },
          '@page': { size: 'A2 portrait', margin: 0 },
        }}
      />

      <Box className="poster-root" sx={{ maxWidth: 720, mx: 'auto' }}>
        <Stack
          direction="row"
          className="no-print"
          sx={{
            justifyContent: 'flex-end',
            mb: 2,
          }}
        >
          <Button variant="contained" startIcon={<IoPrintOutline />} onClick={handlePrint} sx={{ borderRadius: 999, fontWeight: 700, boxShadow: 'none' }}>
            印刷 / PDF出力 (A2)
          </Button>
        </Stack>

        <Stack spacing={4} className="poster-page">
          <PosterHeader />
          <Hero />
          <Benefits />
          <Notifications />
          <Management />
          <PriceAndCta />
        </Stack>
      </Box>
    </>
  )
}

const PosterHeader = () => {
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Stack
        direction="row"
        spacing={2.5}
        sx={{
          alignItems: 'center',
        }}
      >
        <Box component="img" src="/logomark-color.svg" alt={serviceName} sx={{ height: 72 }} />
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 44, lineHeight: 1.05 }}>{serviceName}</Typography>
          <Typography sx={{ color: '#333', fontSize: 20 }}>IoT罠とその管理サービス</Typography>
        </Box>
      </Stack>
      <Box sx={{ px: 3, py: 1, borderRadius: 999, backgroundColor: 'primary.main', color: '#fff', fontSize: 22, fontWeight: 700 }}>
        {deviceCampaignLabel}実施中
      </Box>
    </Stack>
  )
}

const Hero = () => {
  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: 6, overflow: 'hidden', border: '1px solid', borderColor: 'divider', background: `linear-gradient(135deg, ${TINT} 0%, #FFFFFF 62%)` }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'stretch',
        }}
      >
        <Box sx={{ flex: 1.25, p: 5, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="overline" sx={{ color: 'primary.dark', fontSize: 20, lineHeight: 1.2 }}>
            LTEモデル
          </Typography>
          <Typography component="h1" sx={{ fontWeight: 900, fontSize: 60, lineHeight: 1.15, mt: 1, letterSpacing: '-0.01em' }}>
            見回りの負担を、
            <br />
            大幅に軽減。
          </Typography>
          <Typography sx={{ mt: 3, fontSize: 23, color: '#333', lineHeight: 1.7 }}>
            くくり罠・箱罠に取り付けるだけ。罠の作動をスマホやメールにお知らせ。親機・中継機は不要、単独で動作する低価格なIoT罠です。
          </Typography>
        </Box>
        <Box
          className="poster-hero-media"
          sx={{ flex: 1, minHeight: 380, display: 'flex', flexDirection: 'column', backgroundColor: (theme) => theme.palette.grey[300], p: 3 }}
        >
          <Box
            component="img"
            src="/wanapp_device_image.png"
            alt="LTEモデル"
            sx={{ flex: 1, minHeight: 0, maxWidth: '100%', objectFit: 'contain', display: 'block' }}
          />
          <Typography sx={{ mt: 1, textAlign: 'right', color: '#333', fontSize: 16 }}>※画像はイメージです</Typography>
        </Box>
      </Stack>
    </Paper>
  )
}

type BenefitProps = { icon: ReactElement; title: string; body: string }
const BENEFITS: BenefitProps[] = [
  { icon: <IoCashOutline />, title: '通信費0円', body: '月額料金なし。約10年分の通信費を本体価格に含みます。' },
  { icon: <IoWifiOutline />, title: '親機・中継機 不要', body: 'LTE-M回線で単独動作。難しい設置や設定は不要です。' },
  { icon: <IoBatteryFullOutline />, title: '電池で長期稼働', body: '単3電池3本で約3〜4ヶ月。手軽に運用できます。' },
]

const Benefits = () => {
  return (
    <Stack direction="row" spacing={3}>
      {BENEFITS.map((b) => (
        <Paper key={b.title} elevation={0} sx={{ flex: 1, p: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', fontSize: 40, color: 'primary.dark' }}>{b.icon}</Box>
            <Typography sx={{ fontWeight: 800, fontSize: 27 }}>{b.title}</Typography>
          </Stack>
          <Typography sx={{ fontSize: 20, lineHeight: 1.6, color: '#333', mt: 1.5 }}>{b.body}</Typography>
        </Paper>
      ))}
    </Stack>
  )
}

const Notifications = () => {
  return (
    <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', p: 3 }}>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', fontSize: 34, color: 'primary.dark' }}>
          <IoNotificationsOutline />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 28 }}>3つの通知でしっかり見守り</Typography>
        <Typography sx={{ color: '#333', fontSize: 18 }}>メール / LINE / 管理画面に対応</Typography>
      </Stack>
      <Stack direction="row" spacing={2.5}>
        <NoticeItem title="罠の作動 (捕獲)" body="罠が作動したことを検知し、いち早くお知らせ。" />
        <NoticeItem title="故障・電池切れ" body="不具合や電池切れの可能性を検知して通知。" />
        <NoticeItem title="電源ON" body="デバイスの起動を検知して通知。" />
      </Stack>
    </Paper>
  )
}

const NoticeItem = ({ title, body }: { title: string; body: string }) => {
  return (
    <Box sx={{ flex: 1, pl: 2.5, borderLeft: '6px solid', borderLeftColor: 'primary.main' }}>
      <Typography sx={{ fontWeight: 700, fontSize: 22 }}>{title}</Typography>
      <Typography sx={{ fontSize: 18, lineHeight: 1.6, color: '#333', mt: 0.5 }}>{body}</Typography>
    </Box>
  )
}

const Management = () => {
  return (
    <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', p: 3 }}>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', fontSize: 34, color: 'primary.dark' }}>
          <IoPeopleOutline />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 28 }}>管理Webアプリ</Typography>
        <Typography sx={{ color: '#333', fontSize: 18 }}>無料でご利用可能</Typography>
      </Stack>
      <Stack direction="row" spacing={2.5}>
        <NoticeItem title="グループで共有" body="個人から自治体・猟友会まで、メンバーでデバイスを共有管理。" />
        <NoticeItem title="Webで一元管理" body="デバイスを管理でき、一覧画面で全機の状態を一目で確認できます。" />
        <NoticeItem title="サブグループで出し分け" body="デバイスごとに通知先を分けて送れます。（有料プラン）" />
      </Stack>
    </Paper>
  )
}

const PriceAndCta = () => {
  return (
    <Paper className="poster-foot" elevation={0} sx={{ borderRadius: 6, border: '1px solid', borderColor: 'divider', backgroundColor: TINT, p: 5 }}>
      <Stack
        direction="row"
        spacing={5}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* 価格 */}
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: 'inline-block',
              px: 2.5,
              py: 1,
              mb: 2,
              borderRadius: 999,
              backgroundColor: 'primary.main',
              color: '#fff',
              fontSize: 20,
              fontWeight: 700,
            }}
          >
            {deviceCampaignLabel}
          </Box>
          <Stack
            direction="row"
            spacing={2.5}
            sx={{
              alignItems: 'baseline',
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ color: '#333', textDecoration: 'line-through', fontSize: 28 }}>定価 {devicePriceNumber}円</Typography>
            <Typography sx={{ fontWeight: 900, fontSize: 90, color: 'primary.dark', lineHeight: 1 }}>{deviceCampaignPriceNumber}円</Typography>
            <Typography sx={{ color: '#333', fontSize: 20 }}>(税込)</Typography>
          </Stack>
          <Typography sx={{ fontSize: 20, color: '#333', mt: 2, lineHeight: 1.6 }}>
            デバイス本体価格。管理Webアプリは無料 / 毎月の通信費0円 (約10年分を本体価格に含む)。
            {supportEmail && (
              <>
                <br />
                お問い合わせ:{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {supportEmail}
                </Box>
              </>
            )}
          </Typography>
        </Box>

        {/* QR */}
        <Stack
          spacing={1.5}
          sx={{
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Box className="poster-qr" sx={{ p: 2.5, backgroundColor: '#fff', borderRadius: 3, border: '1px solid', borderColor: 'divider', lineHeight: 0 }}>
            <QRCodeSVG value={baseURL} size={190} />
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>スマホで詳しく</Typography>
            <Typography sx={{ color: '#333', fontSize: 18 }}>{baseURL}</Typography>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  )
}
